import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage, API } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileDown, Scale, Landmark, ShieldCheck, FileText } from "lucide-react";
import { toast } from "sonner";

/**
 * R154 — State disclosure packet generator.
 * FL 720.303 · CA Davis-Stirling · TX 202.001 · GA POA.
 * Preview snapshot from live DB → generate packet → PDF download.
 */
export default function StateDisclosurePage() {
  const { properties, propertyId } = useProperty();
  const [scope, setScope] = useState(propertyId || properties[0]?.id || "");
  const [state, setState] = useState("FL");
  const [preview, setPreview] = useState(null);
  const [packets, setPackets] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (properties.length && !scope) setScope(properties[0].id);
  }, [properties, scope]);

  const load = useCallback(async () => {
    if (!scope) return;
    try {
      const [pre, hist] = await Promise.all([
        api.get("/admin/state-disclosure/preview", { params: { property_id: scope, state } }),
        api.get("/admin/state-disclosure", { params: { property_id: scope } }),
      ]);
      setPreview(pre.data);
      setPackets(hist.data.items || []);
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to load"); }
  }, [scope, state]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/state-disclosure/generate",
        { property_id: scope, state });
      toast.success("Disclosure packet generated");
      window.open(`${API}/admin/state-disclosure/${data.packet.id}.pdf`,
                   "_blank", "noopener,noreferrer");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Generate failed"); }
    finally { setBusy(false); }
  };

  const scopeName = useMemo(
    () => properties.find((p) => p.id === scope)?.name || scope,
    [properties, scope],
  );

  return (
    <div className="space-y-4" data-testid="disclosure-page">
      <PageHeader
        eyebrow="Legal"
        title="State Disclosure Packet"
        subtitle="Buyer's package required by state law — one branded PDF, live-populated from your data."
      />

      <div className="flex items-center gap-2 flex-wrap">
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger className="w-56 h-9 bg-white/[0.04] border-white/10"
                          data-testid="disclosure-scope"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
            {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={state} onValueChange={setState}>
          <SelectTrigger className="w-40 h-9 bg-white/[0.04] border-white/10"
                          data-testid="disclosure-state"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
            <SelectItem value="FL">FL · Florida</SelectItem>
            <SelectItem value="CA">CA · California</SelectItem>
            <SelectItem value="TX">TX · Texas</SelectItem>
            <SelectItem value="GA">GA · Georgia</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={generate} disabled={busy}
                className="ml-auto bg-orange-500 hover:bg-orange-600 rounded-full h-9"
                data-testid="disclosure-generate-btn">
          <FileDown className="h-4 w-4 mr-1" /> Generate packet
        </Button>
      </div>

      {preview && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="border-white/10 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Scale className="h-4 w-4 text-orange-400" />
              <div className="overline">{preview.state.name} statute</div>
            </div>
            <div className="font-mono text-xs text-orange-300 mb-1">{preview.state.citation}</div>
            <div className="text-xs text-white/60 mt-3 mb-1">Required documents:</div>
            <ul className="text-xs list-decimal ml-5 space-y-1 text-white/80">
              {preview.state.required_docs.map((d, i) => <li key={i}>{d}</li>)}
            </ul>
          </Card>

          <Card className="border-white/10 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Landmark className="h-4 w-4 text-orange-400" />
              <div className="overline">Live snapshot — {scopeName}</div>
            </div>
            <dl className="grid grid-cols-2 gap-y-2 gap-x-4 text-xs">
              <dt className="text-white/50">Total units</dt>
              <dd className="text-white">{preview.snapshot.total_units}</dd>
              <dt className="text-white/50">Avg. monthly dues</dt>
              <dd className="text-white tabular-nums">${preview.snapshot.monthly_dues_avg_usd?.toFixed?.(2) || 0}</dd>
              <dt className="text-white/50">Reserve % funded</dt>
              <dd className="text-white">{preview.snapshot.reserve_percent_funded?.toFixed?.(1) || 0}%</dd>
              <dt className="text-white/50">Reserve balance</dt>
              <dd className="text-white tabular-nums">${(preview.snapshot.reserve_balance_usd || 0).toLocaleString()}</dd>
              <dt className="text-white/50">Pending SAs</dt>
              <dd className="text-white tabular-nums">${(preview.snapshot.pending_special_assessments_usd || 0).toLocaleString()}</dd>
              <dt className="text-white/50">Transfer fee</dt>
              <dd className="text-white tabular-nums">${(preview.snapshot.transfer_fee_usd || 0).toLocaleString()}</dd>
              <dt className="text-white/50">Capital contribution</dt>
              <dd className="text-white tabular-nums">${(preview.snapshot.capital_contribution_usd || 0).toLocaleString()}</dd>
              <dt className="text-white/50">Insurance carrier</dt>
              <dd className="text-white">{preview.snapshot.insurance_carrier || <span className="text-white/40">—</span>}</dd>
            </dl>
          </Card>
        </div>
      )}

      <Card className="border-white/10 p-0 overflow-hidden">
        <div className="p-4 border-b border-white/10 flex items-center gap-2">
          <FileText className="h-4 w-4 text-orange-400" />
          <div className="overline">History</div>
          <Badge variant="outline" className="text-[10px] border-white/10">{packets.length}</Badge>
        </div>
        {packets.length === 0 ? (
          <div className="p-6 text-sm text-white/40 text-center">No packets generated yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] text-xs">
              <tr>
                <th className="text-left px-3 py-2 text-white/60">State</th>
                <th className="text-left px-3 py-2 text-white/60">Generated</th>
                <th className="text-left px-3 py-2 text-white/60">By</th>
                <th className="text-right px-3 py-2 text-white/60"></th>
              </tr>
            </thead>
            <tbody>
              {packets.map((p) => (
                <tr key={p.id} className="border-t border-white/5" data-testid={`disclosure-row-${p.id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{p.state}</td>
                  <td className="px-3 py-2 text-xs">{new Date(p.generated_at).toLocaleString()}</td>
                  <td className="px-3 py-2 text-xs">{p.generated_by_name}</td>
                  <td className="px-3 py-2 text-right">
                    <a href={`${API}/admin/state-disclosure/${p.id}.pdf`}
                       target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-orange-500/40 text-orange-300 hover:bg-orange-500/10"
                       data-testid={`disclosure-pdf-${p.id}`}>
                      <FileDown className="h-3 w-3" /> PDF
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
